import type { StorefrontProductSummary } from '@erp/shared';

/**
 * Client-side "recently viewed" products for the public storefront, kept in
 * localStorage and scoped per store slug. Display-only — a compact product
 * summary is stored when a product page is opened, newest first, capped.
 */
const KEY = 'erp-store-recent';
const MAX = 12;

type Store = Record<string, StorefrontProductSummary[]>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : {};
    return v && typeof v === 'object' ? (v as Store) : {};
  } catch {
    return {};
  }
}

function write(s: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* localStorage full / unavailable — recently-viewed is non-essential */
  }
}

/** Record a product the customer just opened (dedupes, newest first, capped). */
export function recordRecent(slug: string, product: StorefrontProductSummary): void {
  if (!slug || !product?.id) return;
  const store = read();
  const list = (store[slug] ?? []).filter((p) => p.id !== product.id);
  list.unshift(product);
  store[slug] = list.slice(0, MAX);
  write(store);
}

/** The recently-viewed products for a store, optionally excluding one id. */
export function getRecent(slug: string, excludeId?: string): StorefrontProductSummary[] {
  return (read()[slug] ?? []).filter((p) => p.id !== excludeId);
}
